// @flow

import type {DistributionChannel, DistributionChannels} from "./DistributionChannels";
import type Price from "./Price";
import type {UnitPrice} from "./UnitPrice";
import {SHIPPING_TYPES} from "./ShippingTypes";
import type {TireLabel} from "./TireLabel";
import type {ManufacturerWarranty} from "./ManufacturerWarranty";

export type SlImage = {
    url: string,
    name: string,
    attachmentUrl: string,
    previewImage: ?SlImage,
    caption?: string
};

export type Product = {
    id: string,
    dealerId: string,
    articleNumber: string,
    name: string,
    description: string,
    distributionChannels: DistributionChannels,
    fitments: string[],
    fittingCarPaths?: string[],
    carlines: string[],
    galleryImage: SlImage,
    vatRate: ?number,
    price: ?Price,
    recommendedRetailPrice: Price,
    historicMinimalPurchasePrice: Price,
    unitPrice: ?UnitPrice,
    isAvailable: boolean,
    availableQuantity: ?number,
    tireLabel: TireLabel,
    trademark: string,
    markers: string[],
    manufacturerWarranty: ?ManufacturerWarranty,
}

// stock quantity semantics: null / -1 = unlimited (not stock managed), >= 0 = managed stock (single item = 1)
export const getMaxOrderableQuantity = (product: Product): number => {
    const quantity = product.availableQuantity;
    return (quantity == null || quantity < 0) ? Number.POSITIVE_INFINITY : quantity;
};

export const isStockLimited = (product: Product): boolean => {
    const quantity = product.availableQuantity;
    return quantity != null && quantity >= 0;
};

const NO_PRICE_AVAILABLE = "";

export const getGalleryPrice = (product: Product, shippingMethod: ?string) => {
    const {
        pickup,
        shipping,
        mounting
    } = product.distributionChannels;

    switch (true) {
        case (shippingMethod === SHIPPING_TYPES.SHIPPING && shipping != null):
            return product.distributionChannels.shipping.price;
        case (shippingMethod === SHIPPING_TYPES.MOUNT && mounting != null):
            return product.distributionChannels.mounting.price;
        case (shippingMethod === SHIPPING_TYPES.PICKUP && pickup != null):
            return product.distributionChannels.pickup.price;
        case (mounting != null):
            return product.distributionChannels.mounting.price;
        case (shipping != null):
            return product.distributionChannels.shipping.price;
        case (pickup != null):
            return product.distributionChannels.pickup.price;
        case (product.price != null):
            return product.price;
        default:
            return NO_PRICE_AVAILABLE;
    }
};

export const isDiscountedForShippingMethod = (product: Product, shippingMethod: string): boolean => {
	const {pickup, shipping, mounting} = product.distributionChannels;

	let channel = null;
	switch (shippingMethod) {
		case SHIPPING_TYPES.SHIPPING:
			channel = shipping;
			break;
		case SHIPPING_TYPES.MOUNT:
			channel = mounting;
			break;
		case SHIPPING_TYPES.PICKUP:
			channel = pickup;
			break;
		default:
			break;
	}

	return !!(channel && channel.discountRate && channel.discountRate > 0);
};

export const isDiscounted = (product: Product): boolean => {
    return  isDiscountedForShippingMethod(product, SHIPPING_TYPES.PICKUP) ||
            isDiscountedForShippingMethod(product, SHIPPING_TYPES.MOUNT) ||
            isDiscountedForShippingMethod(product, SHIPPING_TYPES.SHIPPING);
}

export const getHistoricMinimalPrice = (product: Product, shippingMethod: string): Price => {
    const {pickup, shipping, mounting} = product.distributionChannels;

    let channel = null;
    switch (shippingMethod) {
        case SHIPPING_TYPES.SHIPPING:
            channel = shipping;
            break;
        case SHIPPING_TYPES.MOUNT:
            channel = mounting;
            break;
        case SHIPPING_TYPES.PICKUP:
            channel = pickup;
            break;
        default:
            break;
    }

    return channel ? channel.historicMinimalPrice : undefined;
}

export const isOnSale = (product: Product): boolean => {
    return product.markers && product.markers.includes("SALE");
}

export const determineDistributionChannelForPriceDisplay = (product: Product) : DistributionChannel => {
    if(!product) {
        return null;
    }
    const {distributionChannels} = product;
    return distributionChannels.pickup ?
        distributionChannels.pickup :
        distributionChannels.mounting ?
            distributionChannels.mounting :
            distributionChannels.shipping;
}

